import {
  WSActions,
  WSControl,
  WSControlGroup,
  WSForm,
  WSFormProps,
  WSInfoBox,
  WSList,
  WSPanel,
  WSSectionToolbar,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import merge from "lodash/merge";
import { COUNTRY_OPTIONS } from "../../../../constants/user";
import { getEntityTypeLabel } from "../../utils/entityType";
import { emptyValues } from "./emptyValues";
import { getValidationSchema } from "./validationSchema";

export type FormDataAccountType = {
  country: string;
  type: "Business" | "Individual";
  representativeConfirmation: boolean;
  isUsPerson: boolean;
};

type Props = {
  defaultValues?: WSFormProps<FormDataAccountType>["defaultValues"];
  onSubmit?: WSFormProps<FormDataAccountType>["onSubmit"];
  onBack?: () => void;
  onChange: (data: FormDataAccountType) => void;
  accountTypes?: ("Individual" | "Business")[];
  verificationLevelIsBanking?: boolean;
  disabled?: boolean;
  countryDisabled?: boolean;
  // Controls the "Are you a U.S. person?" card shown to non-US individuals.
  // When omitted/false the card and checkbox are hidden, so every consumer that
  // needs U.S. person collection must opt in explicitly.
  allowUSInternationalPerson?: boolean;
};

export const FormAccountType: React.FC<Props> = ({
  defaultValues,
  onSubmit,
  onChange,
  onBack,
  verificationLevelIsBanking,
  accountTypes = ["Business", "Individual"],
  disabled,
  countryDisabled,
  allowUSInternationalPerson
}) => {
  // Default to "Individual" when it's an allowed type, but fall back to the
  // only permitted type for business-only flows (e.g. payer onboarding) so the
  // pre-selected radio option always remains a valid, selectable choice.
  const defaultType = accountTypes.includes("Individual")
    ? "Individual"
    : accountTypes[0];

  return (
    <WSForm<FormDataAccountType>
      defaultValues={merge(
        { ...emptyValues, type: defaultType },
        defaultValues
      )}
      onSubmit={onSubmit}
      validationSchema={getValidationSchema()}
    >
      {formContext => {
        const handleChange = () => {
          const values = formContext.getValues();
          onChange(values);
        };

        const handleCountryChange = () => {
          const values = formContext.getValues();
          if (values.country === "US") {
            formContext.setValue("isUsPerson", false);
          }
          onChange(formContext.getValues());
        };

        const handleTypeChange = () => {
          const values = formContext.getValues();
          if (values.type === "Business") {
            formContext.setValue("isUsPerson", false);
          }
          onChange(formContext.getValues());
        };

        return (
          <WSList gap="2XL">
            <WSPanel>
              <WSList gap="2XL">
                <WSSectionToolbar title="Account type" />

                <WSForm.Field
                  name="country"
                  component={WSSelect}
                  onChange={handleCountryChange}
                  label="Country of operation"
                  componentProps={{
                    disabled: disabled || countryDisabled,
                    required: true,
                    internalSearch: true,
                    options: COUNTRY_OPTIONS
                  }}
                />
                <WSForm.Value name="country">
                  {country => (
                    <WSForm.Field
                      name="type"
                      component={WSControlGroup}
                      onChange={handleTypeChange}
                      componentProps={{
                        required: true,
                        type: "radio",
                        disabled,
                        chip: true,
                        options: accountTypes.map(type =>
                          type === "Business"
                            ? {
                                label: getEntityTypeLabel("Business", country),
                                value: "Business",
                                tooltip:
                                  "Your entity information will be used on invoices and tax documentation"
                              }
                            : {
                                label: getEntityTypeLabel(
                                  "Individual",
                                  country
                                ),
                                value: "Individual",
                                tooltip:
                                  "Your personal information will be used on invoices and tax documentation"
                              }
                        )
                      }}
                    />
                  )}
                </WSForm.Value>

                <WSForm.Value name="type">
                  {type =>
                    type === "Business" &&
                    verificationLevelIsBanking && (
                      <WSInfoBox title="Important:">
                        <p>
                          To create an account, you must be an authorized
                          representative of a business.
                        </p>

                        <p>
                          Only individuals with the authority to act on behalf
                          of a company or organization are eligible to register.
                          By proceeding with account creation, you confirm that
                          you have the legal right to represent and make
                          decisions for the business in question.
                        </p>

                        <p>
                          Ensure you have all beneficial owner(s) information to
                          complete the account creation process.
                        </p>

                        <WSForm.Field
                          name="representativeConfirmation"
                          component={WSControl}
                          onChange={handleChange}
                          componentProps={{
                            type: "checkbox",
                            size: "S",
                            label:
                              "I confirm that I am an authorized representative of the business"
                          }}
                        />
                      </WSInfoBox>
                    )
                  }
                </WSForm.Value>

                <WSForm.Value name="country">
                  {country =>
                    country !== "US" && (
                      <WSInfoBox title="Important:">
                        The above account type settings cannot be changed
                        without re-verification once the account creation
                        process is completed, please review your selections
                        before the final review and submit step.
                      </WSInfoBox>
                    )
                  }
                </WSForm.Value>
              </WSList>
            </WSPanel>

            <WSForm.Values names={["country", "type"]}>
              {values =>
                allowUSInternationalPerson &&
                values.country !== "US" &&
                values.type === "Individual" && (
                  <WSPanel>
                    <WSList gap="M">
                      <WSText.Heading5>Are you a U.S. person?</WSText.Heading5>
                      <WSText.ParagraphSm color="gray500">
                        Only check the box to confirm you're a U.S. citizen,
                        green card holder, or U.S. tax resident—even if you live
                        in another country.
                      </WSText.ParagraphSm>
                      <WSForm.Field
                        name="isUsPerson"
                        component={WSControl}
                        onChange={handleChange}
                        componentProps={{
                          type: "checkbox",
                          size: "S",
                          label: "Yes, I am a U.S. person",
                          description:
                            "In addition to your local tax ID, we'll collect your U.S. Social Security Number (SSN) in the next step."
                        }}
                      />
                    </WSList>
                  </WSPanel>
                )
              }
            </WSForm.Values>

            <WSForm.Values names={["type", "representativeConfirmation"]}>
              {values => (
                <WSActions
                  alignment="fill"
                  buttons={[
                    {
                      label: "Back",
                      onClick: onBack,
                      kind: "Secondary",
                      type: "button",
                      visible: !!onBack
                    },
                    {
                      label: "Continue",
                      type: "submit",
                      disabled:
                        values.type === "Business" &&
                        verificationLevelIsBanking &&
                        !values.representativeConfirmation
                    }
                  ]}
                />
              )}
            </WSForm.Values>
          </WSList>
        );
      }}
    </WSForm>
  );
};
